import { kebabCase } from 'lodash';
import { CSS_PROPS, GLOBAL_BLOCK_DISPLAY, IMG_TINT, IMG_TINT_ATTR, SHAPE_LINE_THICKNESS } from 'common/constants';
import { BBUiConfig, ComponentTypes } from 'types/story';
import {
	FIELD_BB_ERROR_STYLE,
	FIELD_BB_STYLE,
	FIELD_DATA_ATTR,
	FIELD_ERROR_DATA_ATTR,
} from 'client/components/common/BuildingBlocks/Fields/constants';
import { getTimerColorProperty } from 'client/components/common/BuildingBlocks/Timer/Timer';

type SelectorPostfix = () => string;
type SelectorPrefix = (params?: { cardId?: string }) => string;
type CustomProperties = Record<
	string,
	{
		selectorPostfix?: SelectorPostfix;
		selectorPrefix?: SelectorPrefix;
		cssProperty: string;
	}
>;

/**
 * Regular CSS properties are applied to original building block node.
 * But there are douses of cases when you may need to apply/read some property to some node that is inside original one,
 * next to it or to original one which is only at some specific css path.
 *
 * This is a list of properties that behave like regular CSS properties, but are handled in a custom way.
 * This may be a non-valid property that will be transformed into a valid one for a certain node,
 * or it may be a valid property that will be applied to the modified selector.
 *
 */
export function getCustomCssProp(
	type: ComponentTypes,
	prop: string,
	extra?: { uiConfig?: BBUiConfig }
): CustomProperties[string] | undefined {
	const common: CustomProperties = {
		[CSS_PROPS.timer.trackColor]: {
			selectorPostfix: () => '[data-selector="timer-track"]',
			cssProperty: getTimerColorProperty(extra?.uiConfig),
		},
		[CSS_PROPS.timer.progressColor]: {
			selectorPostfix: () => '[data-selector="timer-progress"]',
			cssProperty: getTimerColorProperty(extra?.uiConfig),
		},
		[CSS_PROPS.timer.strokeWidth]: {
			selectorPostfix: () => 'circle',
			cssProperty: 'stroke-width',
		},
		[SHAPE_LINE_THICKNESS]: {
			selectorPostfix: () => '[data-shape="line"]',
			cssProperty: 'height',
		},
		[GLOBAL_BLOCK_DISPLAY]: {
			selectorPrefix: params => `[data-card-id="${params?.cardId}"]`,
			cssProperty: 'display',
		},
		[FIELD_BB_ERROR_STYLE.TEXT.COLOR]: {
			selectorPostfix: () => `[${FIELD_ERROR_DATA_ATTR}]`,
			cssProperty: 'color',
		},
		[FIELD_BB_ERROR_STYLE.TEXT.FONT_FAMILY]: {
			selectorPostfix: () => `[${FIELD_ERROR_DATA_ATTR}]`,
			cssProperty: 'font-family',
		},
		[FIELD_BB_ERROR_STYLE.TEXT.FONT_WEIGHT]: {
			selectorPostfix: () => `[${FIELD_ERROR_DATA_ATTR}]`,
			cssProperty: 'font-weight',
		},
		[FIELD_BB_ERROR_STYLE.TEXT.FONT_SIZE]: {
			selectorPostfix: () => `[${FIELD_ERROR_DATA_ATTR}]`,
			cssProperty: 'font-size',
		},
		[FIELD_BB_ERROR_STYLE.TEXT.LINE_HEIGHT]: {
			selectorPostfix: () => `[${FIELD_ERROR_DATA_ATTR}]`,
			cssProperty: 'line-height',
		},
		[FIELD_BB_ERROR_STYLE.TEXT.TEXT_ALIGN]: {
			selectorPostfix: () => `[${FIELD_ERROR_DATA_ATTR}]`,
			cssProperty: 'text-align',
		},
		[FIELD_BB_ERROR_STYLE.TEXT.DIRECTION]: {
			selectorPostfix: () => `[${FIELD_ERROR_DATA_ATTR}]`,
			cssProperty: 'direction',
		},
		[FIELD_BB_ERROR_STYLE.PADDING.LEFT]: {
			selectorPostfix: () => `[${FIELD_ERROR_DATA_ATTR}]`,
			cssProperty: 'padding-left',
		},
		[FIELD_BB_ERROR_STYLE.PADDING.RIGHT]: {
			selectorPostfix: () => `[${FIELD_ERROR_DATA_ATTR}]`,
			cssProperty: 'padding-right',
		},
		[FIELD_BB_ERROR_STYLE.PADDING.TOP]: {
			selectorPostfix: () => `[${FIELD_ERROR_DATA_ATTR}]`,
			cssProperty: 'padding-top',
		},
		[FIELD_BB_ERROR_STYLE.PADDING.BOTTOM]: {
			selectorPostfix: () => `[${FIELD_ERROR_DATA_ATTR}]`,
			cssProperty: 'padding-bottom',
		},
		[FIELD_BB_STYLE.PADDING.LEFT]: {
			selectorPostfix: () => `[${FIELD_DATA_ATTR}]`,
			cssProperty: 'padding-left',
		},
		[FIELD_BB_STYLE.PADDING.RIGHT]: {
			selectorPostfix: () => `[${FIELD_DATA_ATTR}]`,
			cssProperty: 'padding-right',
		},
		[FIELD_BB_STYLE.PADDING.TOP]: {
			selectorPostfix: () => `[${FIELD_DATA_ATTR}]`,
			cssProperty: 'padding-top',
		},
		[FIELD_BB_STYLE.PADDING.BOTTOM]: {
			selectorPostfix: () => `[${FIELD_DATA_ATTR}]`,
			cssProperty: 'padding-bottom',
		},
		[FIELD_BB_STYLE.TEXT.PH_COLOR]: {
			selectorPostfix: () => `::placeholder`,
			cssProperty: 'color',
		},
		[IMG_TINT]: {
			selectorPostfix: () => `[${IMG_TINT_ATTR}]`,
			cssProperty: 'backgroundColor',
		},
		[FIELD_BB_STYLE.BOX.FILL]: {
			selectorPostfix: () => '[data-checkbox="box"]',
			cssProperty: 'background-color',
		},
		[FIELD_BB_STYLE.BOX.STROKE]: {
			selectorPostfix: () => '[data-checkbox="box"]',
			cssProperty: 'border-color',
		},
		[FIELD_BB_STYLE.BOX.ICON_COLOR]: {
			selectorPostfix: () => '[data-checkbox="box"]::after',
			cssProperty: 'border-color',
		},
	};

	switch (type) {
		case 'TEXT_AREA_FIELD':
		case 'TEXT_FIELD':
			return {
				...common,
				...[...Object.values(CSS_PROPS.borders), ...Object.values(CSS_PROPS.background)].reduce(
					(acc, property) => {
						acc[property] = {
							selectorPostfix: () => (type === 'TEXT_AREA_FIELD' ? 'textarea' : 'input'),
							cssProperty: kebabCase(property),
						};
						return acc;
					},
					{}
				),
			}[prop];
		default:
			return common[prop];
	}
}
